import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { AuthProvider, useAuth } from './context/AuthContext';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import AcademicManagement from './pages/AcademicManagement';
import StudentManagement from './pages/StudentManagement';
import TeacherManagement from './pages/TeacherManagement';
import AttendanceManagement from './pages/AttendanceManagement';
import FeeManagement from './pages/FeeManagement';
import AccountingManagement from './pages/AccountingManagement';
import ExaminationManagement from './pages/ExaminationManagement';
import HomeworkManagement from './pages/HomeworkManagement';
import NoticeManagement from './pages/NoticeManagement';
import RatingManagement from './pages/RatingManagement';
import ClinicalManagement from './pages/ClinicalManagement';
import LogBookManagement from './pages/LogBookManagement';
import HostelManagement from './pages/HostelManagement';
import ConfigurationManagement from './pages/ConfigurationManagement';
import CommunicationManagement from './pages/CommunicationManagement';
import ReportManagement from './pages/ReportManagement';
import ClassStudents from './pages/ClassStudents';
import TimetableManagement from './pages/TimetableManagement';
import CalendarManagement from './pages/CalendarManagement';
import ConnectApps from './pages/ConnectApps';
import GalleryManagement from './pages/GalleryManagement';
import LibraryManagement from './pages/LibraryManagement';
import AdmissionManagement from './pages/AdmissionManagement';
import HomePage from './pages/HomePage';
import Layout from './components/Layout';

const theme = createTheme({
  palette: {
    primary: {
      main: '#00796b',
      light: '#48a999',
      dark: '#004c40',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#26a69a',
      light: '#80cbc4',
      dark: '#00695c',
    },
    success: {
      main: '#00796b',
      light: '#60ad5e',
      dark: '#005005',
    },
    error: {
      main: '#d32f2f',
      light: '#ff6659',
      dark: '#9a0007',
    },
    warning: {
      main: '#ed6c02',
      light: '#ff9800',
      dark: '#e65100',
    },
    background: {
      default: '#f5f7fa',
      paper: '#ffffff',
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h4: {
      fontWeight: 600,
    },
    h5: {
      fontWeight: 600,
    },
    h6: {
      fontWeight: 600,
    },
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          borderRadius: 8,
        },
        contained: {
          boxShadow: '0 4px 6px rgba(21, 101, 192, 0.25)',
          '&:hover': {
            boxShadow: '0 6px 10px rgba(21, 101, 192, 0.35)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)',
          borderRadius: 12,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)',
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          '& .MuiTableCell-head': {
            fontWeight: 600,
            backgroundColor: '#00796b',
            color: '#ffffff',
          },
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: '#f5f7fa',
          },
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: '#004d40',
          color: '#ffffff',
        },
      },
    },
  },
});

function PrivateRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return <div>Loading...</div>;
  }

  return user ? children : <Navigate to="/login" />;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<HomePage />} />
      <Route path="/login" element={<Login />} />
      <Route
        path="/admin"
        element={
          <PrivateRoute>
            <Layout />
          </PrivateRoute>
        }
      >
        <Route index element={<Dashboard />} />
        <Route path="admissions" element={<AdmissionManagement />} />
        <Route path="academic" element={<AcademicManagement />} />
        <Route path="students" element={<StudentManagement />} />
        <Route path="class-students" element={<ClassStudents />} />
        <Route path="teachers" element={<TeacherManagement />} />
        <Route path="attendance" element={<AttendanceManagement />} />
        <Route path="timetable" element={<TimetableManagement />} />
        <Route path="calendar" element={<CalendarManagement />} />
        <Route path="connect" element={<ConnectApps />} />
        <Route path="fees" element={<FeeManagement />} />
        <Route path="accounting" element={<AccountingManagement />} />
        <Route path="examinations" element={<ExaminationManagement />} />
        <Route path="homework" element={<HomeworkManagement />} />
        <Route path="notices" element={<NoticeManagement />} />
        <Route path="ratings" element={<RatingManagement />} />
        <Route path="clinical" element={<ClinicalManagement />} />
        <Route path="logbook" element={<LogBookManagement />} />
        <Route path="hostel" element={<HostelManagement />} />
        <Route path="library" element={<LibraryManagement />} />
        <Route path="gallery" element={<GalleryManagement />} />
        <Route path="configuration" element={<ConfigurationManagement />} />
        <Route path="communication" element={<CommunicationManagement />} />
        <Route path="reports" element={<ReportManagement />} />
      </Route>
    </Routes>
  );
}

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
